import dayjs from 'dayjs';
import { useState, useRef } from 'react';
import { useDispatch } from 'react-redux';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  InputAdornment,
  IconButton,
  useTheme,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import NewReleasesIcon from '@mui/icons-material/NewReleases';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { useLocalization } from '../common/components/LocalizationProvider';
import { MuiFileInput } from 'mui-file-input';
import EditItemView from './components/EditItemView';
import EditAttributesAccordion from './components/EditAttributesAccordion';
import { useTranslation } from '../common/components/LocalizationProvider';
import SettingsMenu from './components/SettingsMenu';
import { prefixString } from '../common/util/stringUtils';
import { calendarsActions } from '../store';
import { useCatch } from '../reactHelper';
import useSettingsStyles from './common/useSettingsStyles';
import fetchOrThrow from '../common/util/fetchOrThrow';

const formatCalendarTime = (time) => {
  const tzid = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return `TZID=${tzid}:${time.locale('en').format('YYYYMMDDTHHmmss')}`;
};

const parseRule = (rule) => {
  if (rule.endsWith('COUNT=1')) {
    return { frequency: 'ONCE' };
  }
  const fragments = rule.split(';');
  const frequency = fragments[0].substring(11);
  const by = fragments.length > 1 ? fragments[1].split('=')[1].split(',') : null;
  return { frequency, by };
};

const formatRule = (rule) => {
  const by = rule.by && rule.by.join(',');
  switch (rule.frequency) {
    case 'DAILY':
      return `RRULE:FREQ=${rule.frequency}`;
    case 'WEEKLY':
      return `RRULE:FREQ=${rule.frequency};BYDAY=${by || 'SU'}`;
    case 'MONTHLY':
      return `RRULE:FREQ=${rule.frequency};BYMONTHDAY=${by || 1}`;
    default:
      return 'RRULE:FREQ=DAILY;COUNT=1';
  }
};

const updateCalendar = (lines, index, element) =>
  window.btoa(lines.map((e, i) => (i !== index ? e : element)).join('\n'));

const simpleCalendar = () =>
  window.btoa(
    [
      'BEGIN:VCALENDAR',
      'VERSION:2.0',
      'PRODID:-//Traccar//NONSGML Traccar//EN',
      'BEGIN:VEVENT',
      'UID:00000000-0000-0000-0000-000000000000',
      `DTSTART;${formatCalendarTime(dayjs())}`,
      `DTEND;${formatCalendarTime(dayjs().add(1, 'hours'))}`,
      'RRULE:FREQ=DAILY',
      'SUMMARY:Event',
      'END:VEVENT',
      'END:VCALENDAR',
    ].join('\n')
  );

const CalendarPage = () => {
  const { classes } = useSettingsStyles();
  const dispatch = useDispatch();
  const t = useTranslation();
  const { htmlLang } = useLocalization();
  const theme = useTheme();

  const fromRef = useRef(null);
  const toRef = useRef(null);

  const [item, setItem] = useState(null);
  const [file, setFile] = useState(null);

  const decoded = item && item.data && window.atob(item.data);
  const simple = decoded && decoded.indexOf('//Traccar//') > 0;
  const lines = decoded && decoded.split('\n');
  const rule = simple && parseRule(lines[7]);

  const handleFileChange = (newFile) => {
    setFile(newFile);
    if (newFile) {
      const reader = new FileReader();
      reader.onload = (event) => {
        const { result } = event.target;
        setItem({ ...item, data: result.substr(result.indexOf(',') + 1) });
      };
      reader.readAsDataURL(newFile);
    }
  };

  const onItemSaved = useCatch(async () => {
    const response = await fetchOrThrow('/api/calendars');
    dispatch(calendarsActions.refresh(await response.json()));
  });

  const validate = () => item && item.name && (item.id || item.data);

  return (
    <EditItemView
      endpoint="calendars"
      item={item}
      setItem={setItem}
      defaultItem={{ data: simpleCalendar() }}
      validate={validate}
      onItemSaved={onItemSaved}
      menu={<SettingsMenu />}
      breadcrumbs={['settingsTitle', 'sharedCalendar']}
    >
      {item && (
        <>
          <Accordion defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box display="flex" alignItems="center" gap={1}>
                <NewReleasesIcon color="primary" />
                <Typography variant="subtitle1">{t('sharedRequired')}</Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails className={classes.details}>
              <Box
                display="flex"
                flexDirection="column"
                gap={2}
                width="100%"
              >
                {/* Linha 1 - Nome e Tipo */}
                <Box
                  display="flex"
                  width="100%"
                  gap={2}
                  sx={{ flexDirection: { xs: 'column', sm: 'row' } }}
                >
                  <Box flex={1} minWidth={0}>
                    <TextField
                      fullWidth
                      value={item.name || ''}
                      onChange={(event) => setItem({ ...item, name: event.target.value })}
                      label={t('sharedName')}
                    />
                  </Box>
                  <Box flex={1} minWidth={0}>
                    <FormControl fullWidth>
                      <InputLabel>{t('sharedType')}</InputLabel>
                      <Select
                        label={t('sharedType')}
                        value={simple ? 'simple' : 'custom'}
                        onChange={(e) =>
                          setItem({
                            ...item,
                            data: e.target.value === 'simple' ? simpleCalendar() : null,
                          })
                        }
                      >
                        <MenuItem value="simple">{t('calendarSimple')}</MenuItem>
                        <MenuItem value="custom">{t('reportCustom')}</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>
                  <Box flex={1} minWidth={0} />
                </Box>

                {/* Linha 2 - Campos Simples ou Upload */}
                {simple ? (
                  <>
                    <Box
                      display="flex"
                      width="100%"
                      gap={2}
                      sx={{ flexDirection: { xs: 'column', sm: 'row' } }}
                    >
                      <Box flex={1} minWidth={0}>
                        <TextField
                          fullWidth
                          label={t('reportFrom')}
                          value={dayjs(lines[5].slice(-15)).locale('en').format('YYYY-MM-DDTHH:mm')}
                          onChange={(e) => {
                            const time = formatCalendarTime(dayjs(e.target.value, 'YYYY-MM-DDTHH:mm'));
                            setItem({
                              ...item,
                              data: updateCalendar(lines, 5, `DTSTART;${time}`),
                            });
                          }}
                          InputLabelProps={{ shrink: true }}
                          InputProps={{
                            inputProps: { type: 'datetime-local', lang: htmlLang },
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton
                                  size="small"
                                  edge="end"
                                  onClick={() => {
                                    const el = fromRef.current;
                                    if (el) {
                                      if (typeof el.showPicker === 'function') el.showPicker();
                                      else el.focus();
                                    }
                                  }}
                                >
                                  <CalendarTodayIcon sx={{ color: theme.palette.action.active }} />
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                          inputRef={fromRef}
                          className={classes.dateInput}
                        />
                      </Box>
                      <Box flex={1} minWidth={0}>
                        <TextField
                          fullWidth
                          label={t('reportTo')}
                          value={dayjs(lines[6].slice(-15)).locale('en').format('YYYY-MM-DDTHH:mm')}
                          onChange={(e) => {
                            const time = formatCalendarTime(dayjs(e.target.value, 'YYYY-MM-DDTHH:mm'));
                            setItem({
                              ...item,
                              data: updateCalendar(lines, 6, `DTEND;${time}`),
                            });
                          }}
                          InputLabelProps={{ shrink: true }}
                          InputProps={{
                            inputProps: { type: 'datetime-local', lang: htmlLang },
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton
                                  size="small"
                                  edge="end"
                                  onClick={() => {
                                    const el = toRef.current;
                                    if (el) {
                                      if (typeof el.showPicker === 'function') el.showPicker();
                                      else el.focus();
                                    }
                                  }}
                                >
                                  <CalendarTodayIcon sx={{ color: theme.palette.action.active }} />
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                          inputRef={toRef}
                          className={classes.dateInput}
                        />
                      </Box>
                      <Box flex={1} minWidth={0} />
                    </Box>

                    <Box
                      display="flex"
                      width="100%"
                      gap={2}
                      sx={{ flexDirection: { xs: 'column', sm: 'row' } }}
                    >
                      <Box flex={1} minWidth={0}>
                        <FormControl fullWidth>
                          <InputLabel>{t('calendarRecurrence')}</InputLabel>
                          <Select
                            label={t('calendarRecurrence')}
                            value={rule.frequency}
                            onChange={(e) =>
                              setItem({
                                ...item,
                                data: updateCalendar(lines, 7, formatRule({ frequency: e.target.value })),
                              })
                            }
                          >
                            {['ONCE', 'DAILY', 'WEEKLY', 'MONTHLY'].map((it) => (
                              <MenuItem key={it} value={it}>
                                {t(prefixString('calendar', it.toLowerCase()))}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Box>
                      {['WEEKLY', 'MONTHLY'].includes(rule.frequency) && (
                        <Box flex={1} minWidth={0}>
                          <FormControl fullWidth>
                            <InputLabel>{t('calendarDays')}</InputLabel>
                            <Select
                              multiple
                              label={t('calendarDays')}
                              value={rule.by}
                              onChange={(e) =>
                                setItem({
                                  ...item,
                                  data: updateCalendar(lines, 7, formatRule({ ...rule, by: e.target.value })),
                                })
                              }
                            >
                              {rule.frequency === 'WEEKLY'
                                ? ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'].map(
                                    (it) => (
                                      <MenuItem key={it} value={it.substring(0, 2).toUpperCase()}>
                                        {t(prefixString('calendar', it))}
                                      </MenuItem>
                                    )
                                  )
                                : Array.from({ length: 31 }, (_, i) => i + 1).map((it) => (
                                    <MenuItem key={it} value={String(it)}>
                                      {it}
                                    </MenuItem>
                                  ))}
                            </Select>
                          </FormControl>
                        </Box>
                      )}
                      <Box flex={1} minWidth={0} />
                    </Box>
                  </>
                ) : (
                  <Box
                    display="flex"
                    width="100%"
                    gap={2}
                    sx={{ flexDirection: { xs: 'column', sm: 'row' } }}
                  >
                    <Box flex={1} minWidth={0}>
                      <MuiFileInput
                        fullWidth
                        placeholder={t('sharedSelectFile')}
                        value={file}
                        onChange={handleFileChange}
                      />
                    </Box>
                    <Box flex={1} minWidth={0} />
                    <Box flex={1} minWidth={0} />
                  </Box>
                )}
              </Box>
            </AccordionDetails>
          </Accordion>

          <EditAttributesAccordion
            attributes={item.attributes}
            setAttributes={(attributes) => setItem({ ...item, attributes })}
            definitions={{}}
          />
        </>
      )}
    </EditItemView>
  );
};

export default CalendarPage;
